import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Vote, MessageCircle, Heart, Share2, Flag, Sparkles, X, Plus,
  Loader2, Send, Lock, ChevronRight, Trash2, CornerDownRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import CommunitySidebar from "../components/community/CommunitySidebar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function metaLabel(w) {
  const parts = [];
  if (w.age_band && w.age_band !== "비공개") parts.push(w.age_band);
  if (w.gender && w.gender !== "비공개") parts.push(w.gender);
  return parts.join(" ");
}

function AiBadge({ children = "AI 예시 고민" }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-burgundy-50 text-burgundy-600 text-[10.5px] font-bold border border-burgundy-100" data-testid="ai-example-badge">
      <Sparkles className="w-3 h-3" /> {children}
    </span>
  );
}

// ------------------------------------------------------------------ 카드
function WorryCard({ w, onOpen }) {
  const snippet = (w.content || "").replace(/\n+/g, " ").slice(0, 60);
  return (
    <motion.button
      layout
      onClick={() => onOpen(w.id)}
      className="w-full text-left bg-white rounded-2xl border border-stone-200 p-4 hover:border-burgundy-200 hover:shadow-[0_2px_10px_rgba(0,0,0,0.04)] transition-all"
      data-testid={`worry-card-${w.id}`}
    >
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        {w.is_ai_seed ? <AiBadge /> : (
          w.category ? <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-[10.5px] font-semibold">{w.category}</span> : null
        )}
        <span className="text-[12px] text-stone-500">{metaLabel(w)}</span>
      </div>
      <h3 className="text-[16px] font-bold text-stone-900 leading-snug mb-1">{w.title}</h3>
      <p className="text-[13.5px] text-stone-500 leading-relaxed line-clamp-2">{snippet}…</p>
      <div className="flex items-center gap-4 mt-3 text-[12.5px] text-stone-500">
        <span className="inline-flex items-center gap-1"><Vote className="w-4 h-4 text-burgundy-400" /> {w.vote_total}명</span>
        <span className="inline-flex items-center gap-1"><MessageCircle className="w-4 h-4 text-stone-400" /> {w.comment_count}</span>
        <span className="inline-flex items-center gap-1"><Heart className="w-4 h-4 text-rose-400" /> {w.empathy_count}</span>
        <span className="ml-auto inline-flex items-center gap-0.5 text-burgundy-600 font-semibold">같이 고민해주기 <ChevronRight className="w-4 h-4" /></span>
      </div>
    </motion.button>
  );
}

// ------------------------------------------------------------------ 투표
function VoteSection({ worry, token, user, onVoted }) {
  const [busy, setBusy] = useState(false);
  const voted = worry.my_vote !== null && worry.my_vote !== undefined;

  const cast = async (idx) => {
    if (!user) { toast.error("로그인 후 참여할 수 있어요."); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/worries/${worry.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ option_index: idx }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "투표 실패");
      onVoted(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4" data-testid="worry-vote-section">
      <div className="text-[13px] font-bold text-stone-800 mb-2">여러분이라면 어떻게 하시겠어요?</div>
      <div className="space-y-2">
        {worry.options.map((o) => {
          const mine = worry.my_vote === o.index;
          if (voted) {
            return (
              <div key={o.index} className="relative rounded-xl border border-stone-200 overflow-hidden" data-testid={`worry-vote-result-${o.index}`}>
                <div className={`absolute inset-y-0 left-0 ${mine ? "bg-burgundy-100" : "bg-stone-100"}`} style={{ width: `${o.percent}%` }} />
                <div className="relative flex items-center justify-between px-3.5 py-2.5">
                  <span className={`text-[13.5px] ${mine ? "font-bold text-burgundy-800" : "text-stone-700"}`}>{o.text}{mine && " ✓"}</span>
                  <span className={`text-[13px] font-bold ${mine ? "text-burgundy-700" : "text-stone-500"}`}>{o.percent}%</span>
                </div>
              </div>
            );
          }
          return (
            <button
              key={o.index}
              disabled={busy}
              onClick={() => cast(o.index)}
              className="w-full text-left px-3.5 py-2.5 rounded-xl border border-stone-200 hover:border-burgundy-300 hover:bg-burgundy-50/50 text-[13.5px] text-stone-700 font-medium transition-colors disabled:opacity-50"
              data-testid={`worry-vote-option-${o.index}`}
            >
              {o.text}
            </button>
          );
        })}
      </div>
      <div className="text-[11.5px] text-stone-400 mt-2">{worry.vote_total}명 참여 · 다른 사람들의 생각 보기</div>
    </div>
  );
}

// ------------------------------------------------------------------ 상세 모달
function WorryDetailModal({ id, onClose, onChanged }) {
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [anon, setAnon] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/worries/${id}`, { headers: authHeaders(token) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "불러오기 실패");
      setData(d);
    } catch (e) { toast.error(e.message); onClose(); } finally { setLoading(false); }
  }, [id, token, onClose]);

  useEffect(() => { load(); }, [load]);

  const w = data?.worry;

  const submitComment = async (parentId = null, text = null) => {
    if (!user) { toast.error("로그인 후 참여할 수 있어요."); return; }
    const content = (text ?? comment).trim();
    if (!content) { toast.error("의견 내용을 입력해 주세요."); return; }
    setPosting(true);
    try {
      const res = await fetch(`${API_URL}/api/worries/${id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ content, anonymous: anon, parent_id: parentId }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "등록 실패");
      if (parentId) { setReplyTo(null); setReplyText(""); } else { setComment(""); }
      await load();
      onChanged?.();
      toast.success("의견을 남겼어요");
    } catch (e) { toast.error(e.message); } finally { setPosting(false); }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("이 의견을 삭제할까요?")) return;
    try {
      const res = await fetch(`${API_URL}/api/worries/${id}/comments/${cid}`, { method: "DELETE", headers: authHeaders(token) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "삭제 실패"); }
      await load(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const toggleEmpathy = async () => {
    if (!user) { toast.error("로그인 후 참여할 수 있어요."); return; }
    try {
      const res = await fetch(`${API_URL}/api/worries/${id}/empathy`, { method: "POST", headers: authHeaders(token) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "실패");
      setData((prev) => ({ ...prev, worry: { ...prev.worry, empathy_count: d.empathy_count, empathized: d.empathized } }));
      onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const share = async () => {
    const url = `${window.location.origin}/worries?w=${id}`;
    try { await navigator.clipboard.writeText(url); toast.success("링크를 복사했어요"); }
    catch { toast.message(url); }
  };

  const report = async () => {
    if (!user) { toast.error("로그인 후 이용할 수 있어요."); return; }
    const reason = window.prompt("신고 사유를 입력해 주세요 (선택)", "");
    if (reason === null) return;
    try {
      await fetch(`${API_URL}/api/worries/${id}/report`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ reason }),
      });
      toast.success("신고가 접수되었어요");
    } catch { toast.error("신고에 실패했어요"); }
  };

  const canDelete = (c) => user && (user.role === "admin" || c.author_id === user.id);
  const topComments = (data?.comments || []).filter((c) => !c.parent_id);
  const repliesOf = (cid) => (data?.comments || []).filter((c) => c.parent_id === cid);

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" onClick={onClose} data-testid="worry-detail-modal">
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full sm:max-w-xl bg-[#FBFAF7] rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-stone-100">
          <div className="flex items-center gap-2">
            {w?.is_ai_seed ? <AiBadge /> : (w?.category ? <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 text-[11px] font-semibold">{w.category}</span> : null)}
            <span className="text-[12px] text-stone-500">{w ? metaLabel(w) : ""}</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-100" data-testid="worry-detail-close"><X className="w-5 h-5 text-stone-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !w ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-burgundy-400" /></div>
          ) : (
            <>
              <h2 className="text-[19px] font-bold text-stone-900 leading-snug mb-2.5" data-testid="worry-detail-title">{w.title}</h2>
              <p className="text-[14.5px] text-stone-700 leading-[1.8] whitespace-pre-line">{w.content}</p>

              <VoteSection worry={w} token={token} user={user}
                onVoted={(d) => setData((prev) => ({ ...prev, worry: { ...prev.worry, options: d.options, vote_total: d.vote_total, my_vote: d.my_vote } }))} />

              {/* AI 예시 의견 */}
              {data.ai_opinions?.length > 0 && (
                <div className="mt-6">
                  <div className="text-[13px] font-bold text-stone-800 mb-2">다른 사람들의 생각 보기</div>
                  <div className="space-y-2">
                    {data.ai_opinions.map((op, i) => (
                      <div key={i} className="bg-white rounded-xl border border-stone-200 px-3.5 py-2.5" data-testid={`worry-ai-opinion-${i}`}>
                        <AiBadge>AI 예시 의견</AiBadge>
                        <p className="text-[13.5px] text-stone-700 leading-relaxed mt-1.5">{op}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 실제 회원 의견 */}
              <div className="mt-6">
                <div className="text-[13px] font-bold text-stone-800 mb-2">회원님의 생각은 어떠세요?</div>
                <div className="bg-white rounded-xl border border-stone-200 p-3">
                  <textarea
                    value={comment} onChange={(e) => setComment(e.target.value)}
                    rows={3} placeholder="당신이라면 어떻게 하시겠어요?"
                    className="w-full resize-none text-[14px] text-stone-800 placeholder-stone-400 focus:outline-none bg-transparent"
                    data-testid="worry-comment-input"
                  />
                  <div className="flex items-center justify-between pt-2 border-t border-stone-100 mt-1">
                    <div className="flex items-center gap-3 text-[12.5px]">
                      <button onClick={() => setAnon(false)} className={`px-2.5 py-1 rounded-full font-semibold ${!anon ? "bg-burgundy-50 text-burgundy-700 border border-burgundy-200" : "text-stone-500"}`} data-testid="worry-comment-nickname">닉네임으로</button>
                      <button onClick={() => setAnon(true)} className={`px-2.5 py-1 rounded-full font-semibold ${anon ? "bg-burgundy-50 text-burgundy-700 border border-burgundy-200" : "text-stone-500"}`} data-testid="worry-comment-anon">익명으로</button>
                    </div>
                    <button onClick={() => submitComment()} disabled={posting} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-[13px] font-semibold disabled:opacity-50" data-testid="worry-comment-submit">
                      {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} 내 의견 남기기
                    </button>
                  </div>
                </div>

                <div className="mt-3 space-y-2.5" data-testid="worry-comment-list">
                  {topComments.length === 0 && <div className="text-[13px] text-stone-400 text-center py-3">첫 번째 의견을 남겨보세요.</div>}
                  {topComments.map((c) => (
                    <div key={c.id} className="bg-white rounded-xl border border-stone-200 px-3.5 py-2.5" data-testid={`worry-comment-${c.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[12.5px] font-bold text-stone-700">{c.author}</span>
                        {canDelete(c) && <button onClick={() => deleteComment(c.id)} className="p-1 rounded hover:bg-stone-100" data-testid={`worry-comment-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-stone-400" /></button>}
                      </div>
                      <p className="text-[13.5px] text-stone-700 leading-relaxed mt-1">{c.content}</p>
                      <button onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(""); }} className="text-[11.5px] text-stone-400 hover:text-burgundy-600 font-semibold mt-1.5 inline-flex items-center gap-1" data-testid={`worry-reply-btn-${c.id}`}>
                        <CornerDownRight className="w-3.5 h-3.5" /> 답글
                      </button>

                      {repliesOf(c.id).map((r) => (
                        <div key={r.id} className="ml-4 mt-2 pl-3 border-l-2 border-stone-100" data-testid={`worry-reply-${r.id}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-stone-600">{r.author}</span>
                            {canDelete(r) && <button onClick={() => deleteComment(r.id)} className="p-1 rounded hover:bg-stone-100"><Trash2 className="w-3 h-3 text-stone-400" /></button>}
                          </div>
                          <p className="text-[13px] text-stone-600 leading-relaxed mt-0.5">{r.content}</p>
                        </div>
                      ))}

                      {replyTo === c.id && (
                        <div className="ml-4 mt-2 flex gap-2">
                          <input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="답글을 입력하세요" className="flex-1 px-3 py-1.5 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:border-burgundy-300" data-testid={`worry-reply-input-${c.id}`} />
                          <button onClick={() => submitComment(c.id, replyText)} disabled={posting} className="px-3 py-1.5 rounded-lg bg-burgundy-600 text-white text-[12px] font-semibold disabled:opacity-50" data-testid={`worry-reply-submit-${c.id}`}>등록</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {w && (
          <div className="flex items-center gap-2 px-5 py-3 border-t border-stone-100 bg-white/60">
            <button onClick={toggleEmpathy} className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-semibold border ${w.empathized ? "bg-rose-50 text-rose-600 border-rose-200" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"}`} data-testid="worry-empathy-btn">
              <Heart className={`w-4 h-4 ${w.empathized ? "fill-rose-500 text-rose-500" : ""}`} /> 공감 {w.empathy_count}
            </button>
            <button onClick={share} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white text-stone-600 border border-stone-200 hover:bg-stone-50 text-[13px] font-semibold" data-testid="worry-share-btn"><Share2 className="w-4 h-4" /> 공유</button>
            <button onClick={report} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-stone-400 hover:text-stone-600 text-[12.5px]" data-testid="worry-report-btn"><Flag className="w-4 h-4" /> 신고</button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// ------------------------------------------------------------------ 작성 모달
const CATEGORIES = ["썸", "소개팅", "연애", "연락", "이별", "재혼", "자녀", "전 배우자", "경제 / 조건", "각자 사는 연애", "기타"];
const AGE_BANDS = ["40대", "50대", "60대", "70대 이상", "비공개"];
const GENDERS = ["남성", "여성", "비공개"];

function CreateWorryModal({ onClose, onCreated }) {
  const { token, user } = useAuth();
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [options, setOptions] = useState(["", "", ""]);
  const [ageBand, setAgeBand] = useState("비공개");
  const [gender, setGender] = useState("비공개");
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const runAi = async () => {
    if (content.trim().length < 10) { toast.error("고민 내용을 조금 더 자세히 적어주세요."); return; }
    setAiBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/worries/ai-assist`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ content }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "AI 도움 실패");
      if (d.title) setTitle(d.title);
      if (d.options?.length) setOptions([d.options[0] || "", d.options[1] || "", d.options[2] || ""]);
      toast.success("AI가 제목과 선택지를 만들었어요. 자유롭게 수정하세요.");
    } catch (e) { toast.error(e.message); } finally { setAiBusy(false); }
  };

  const submit = async () => {
    if (!title.trim()) { toast.error("고민 제목을 입력해 주세요."); return; }
    if (!content.trim()) { toast.error("고민 내용을 입력해 주세요."); return; }
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (opts.length < 2) { toast.error("투표 선택지를 최소 2개 이상 입력해 주세요."); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/worries`, {
        method: "POST", headers: { "Content-Type": "application/json", ...authHeaders(token) },
        body: JSON.stringify({ category, title, content, options: opts, age_band: ageBand, gender }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "등록 실패");
      toast.success("고민을 등록했어요");
      onCreated(d.id);
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const Chip = ({ active, children, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`px-3 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${active ? "bg-burgundy-600 text-white border-burgundy-600" : "bg-white text-stone-600 border-stone-200 hover:border-burgundy-300"}`}>{children}</button>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" onClick={onClose} data-testid="worry-create-modal">
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full sm:max-w-lg bg-[#FBFAF7] rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-stone-100">
          <h2 className="font-serif text-lg text-stone-900">익명으로 고민 올리기</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-100"><X className="w-5 h-5 text-stone-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div>
            <div className="text-[13px] font-bold text-stone-800 mb-2">어떤 고민인가요?</div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => <Chip key={c} active={category === c} onClick={() => setCategory(c)} testid={`worry-cat-${c}`}>{c}</Chip>)}
            </div>
          </div>

          <div>
            <div className="text-[13px] font-bold text-stone-800 mb-1.5">고민 내용</div>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} placeholder="어떤 상황인지 편하게 이야기해주세요." className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-[14px] text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300 resize-none" data-testid="worry-create-content" />
            <button onClick={runAi} disabled={aiBusy} className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-burgundy-50 text-burgundy-700 border border-burgundy-200 text-[12.5px] font-bold hover:bg-burgundy-100 disabled:opacity-50" data-testid="worry-ai-assist-btn">
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI로 제목·선택지 만들기
            </button>
          </div>

          <div>
            <div className="text-[13px] font-bold text-stone-800 mb-1.5">고민 제목</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="고민을 한 문장으로 적어주세요." className="w-full rounded-xl border border-stone-200 px-3.5 py-2.5 text-[14px] text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300" data-testid="worry-create-title" />
          </div>

          <div>
            <div className="text-[13px] font-bold text-stone-800 mb-1.5">투표 선택지 (2~3개)</div>
            <div className="space-y-2">
              {options.map((o, i) => (
                <input key={i} value={o} onChange={(e) => { const n = [...options]; n[i] = e.target.value; setOptions(n); }} placeholder={`선택지 ${i + 1}`} className="w-full rounded-xl border border-stone-200 px-3.5 py-2 text-[13.5px] text-stone-800 placeholder-stone-400 focus:outline-none focus:border-burgundy-300" data-testid={`worry-create-option-${i}`} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[13px] font-bold text-stone-800 mb-1.5">연령대</div>
              <div className="flex flex-wrap gap-1.5">{AGE_BANDS.map((a) => <Chip key={a} active={ageBand === a} onClick={() => setAgeBand(a)} testid={`worry-age-${a}`}>{a}</Chip>)}</div>
            </div>
            <div>
              <div className="text-[13px] font-bold text-stone-800 mb-1.5">성별</div>
              <div className="flex flex-wrap gap-1.5">{GENDERS.map((g) => <Chip key={g} active={gender === g} onClick={() => setGender(g)} testid={`worry-gender-${g}`}>{g}</Chip>)}</div>
            </div>
          </div>
          <p className="text-[11.5px] text-stone-400 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> 실명, 이메일, 전화번호 등 실제 개인정보는 공개되지 않습니다.</p>
        </div>
        <div className="px-5 py-3.5 border-t border-stone-100">
          <button onClick={submit} disabled={saving} className="w-full py-3 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-[14px] font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="worry-create-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} 고민 올리기
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ------------------------------------------------------------------ 페이지
export default function WorryRoomPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [real, setReal] = useState([]);
  const [examples, setExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/worries`, { headers: {} });
      const d = await res.json();
      setReal(d.real || []);
      setExamples(d.examples || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ?w= 딥링크로 상세 열기
  useEffect(() => {
    const w = params.get("w");
    if (w) setOpenId(w);
  }, [params]);

  const openCreate = () => {
    if (!user) { toast.error("로그인 후 고민을 올릴 수 있어요."); return; }
    setShowCreate(true);
  };

  const closeDetail = () => { setOpenId(null); if (params.get("w")) { params.delete("w"); setParams(params, { replace: true }); } };

  const hasReal = real.length > 0;

  return (
    <div className="min-h-screen bg-[#F4F2EE] pt-20 pb-32" data-testid="worry-room-page">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex gap-5 items-start">
          <div className="hidden lg:block sticky top-20 self-start max-h-[calc(100vh-5.5rem)] overflow-y-auto pr-1 scrollbar-thin">
            <CommunitySidebar activeCategory="anonymous_advice" onSelectCategory={() => navigate("/community")} />
          </div>

          <main className="flex-1 min-w-0 max-w-2xl mx-auto lg:mx-0">
            {/* Header */}
            <div className="mt-3 sm:mt-5 mb-4 flex items-start justify-between gap-3">
              <div>
                <h1 className="font-serif text-2xl sm:text-3xl text-stone-900 tracking-tight" data-testid="worry-room-heading">익명 고민방</h1>
                <p className="text-[14px] text-stone-500 mt-1.5">혼자 고민하지 마세요. 우리가 함께 생각해 드립니다.</p>
              </div>
              <button onClick={openCreate} className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-[13.5px] font-bold shadow-sm" data-testid="worry-create-open-btn">
                <Plus className="w-4 h-4" /> 익명으로 고민 올리기
              </button>
            </div>

            {loading ? (
              <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-burgundy-400" /></div>
            ) : (
              <div className="space-y-3">
                {real.map((w) => <WorryCard key={w.id} w={w} onOpen={setOpenId} />)}

                {examples.length > 0 && (
                  <>
                    {hasReal && (
                      <div className="flex items-center gap-3 pt-3 pb-1">
                        <div className="h-px flex-1 bg-stone-200" />
                        <span className="text-[12px] font-semibold text-stone-400">이런 고민도 있어요</span>
                        <div className="h-px flex-1 bg-stone-200" />
                      </div>
                    )}
                    {examples.map((w) => <WorryCard key={w.id} w={w} onOpen={setOpenId} />)}
                  </>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      <AnimatePresence>
        {openId && <WorryDetailModal key={openId} id={openId} onClose={closeDetail} onChanged={load} />}
        {showCreate && <CreateWorryModal onClose={() => setShowCreate(false)} onCreated={(id) => { setShowCreate(false); load(); setOpenId(id); }} />}
      </AnimatePresence>

      {/* 항상 접근 가능한 하단 우측 플로팅 버튼 */}
      <button
        onClick={openCreate}
        className="fixed bottom-6 right-5 sm:right-8 z-40 inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-[14px] font-bold shadow-lg shadow-burgundy-900/20 transition-colors"
        data-testid="worry-create-fab"
      >
        <Plus className="w-5 h-5" strokeWidth={2.4} /> 익명으로 고민 올리기
      </button>
    </div>
  );
}
